import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { Tab } from '../../models/utils-tabs';
import { MatIcon } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { filter, Subscription, switchMap, tap } from 'rxjs';
import { TabsConfiguratorComponent } from '../tabs-configurator/tabs-configurator.component';
import { UtilsTabsService } from '../../services/utils-tabs.service';
import { ColumnPickerComponent } from '../tabs-configurator/components/column-picker/column-picker.component';
import { MatFormField, MatInput, MatSuffix } from '@angular/material/input';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ColumnEmitted, Tree } from '../tabs-configurator/components/column-picker/models/column-picker';
import { MetadataService } from '../../../../services/metadata.service';
import { TreeBuilder } from '../tabs-configurator/components/column-picker/tree-builder';
import { UtilsTableService } from '../../services/utils-table.service';
import { CustomFieldsComponent } from '../custom-fields/custom-fields.component';
import { ShareViewComponent } from '../share-view/share-view.component';
import { ExcelExportComponent } from '../excel-export/excel-export.component';
import { Shipment } from '../../../../pages/overview/models/shipment';
import { Container } from '../../../../pages/overview/models/container';
import { MetadataColumn } from '../../../../models/metadata';
import { Order } from '../../../../pages/overview/models/order';
import { OrderException } from '../../../../pages/overview/models/order-exception';

@Component({
  selector: 'app-utils-action-buttons',
  standalone: true,
  templateUrl: './utils-action-buttons.component.html',
  imports: [
    MatButton,
    MatIcon,
    ColumnPickerComponent,
    MatFormField,
    MatInput,
    MatFormField,
    MatIconButton,
    ExcelExportComponent,
    MatSuffix,
    ReactiveFormsModule
  ],
  styleUrl: './utils-action-buttons.component.scss'
})
export class UtilsActionButtonsComponent implements OnInit, OnDestroy {
  @Input() tab: Tab | null = null;
  @Input() tabIndex: number | null = null;
  @Input() selectedItems: Shipment[] | Container[] | Order[] | OrderException[] = [];
  @Input() searchValue: string | null = null;
  @Output() cleared = new EventEmitter<void>();

  searchControl = new FormControl('', { nonNullable: true });

  treeNodes: Tree[] = [];
  selectedColumnKey: string | null = null;

  private treeBuilder = new TreeBuilder('searchable');
  private subscription = new Subscription();

  constructor(
    private matDialog: MatDialog,
    private tabsService: UtilsTabsService,
    private metadataService: MetadataService,
    private utilsTableService: UtilsTableService
  ) {}

  ngOnInit() {
    if (this.searchValue) {
      this.searchControl.setValue(this.searchValue, { emitEvent: false });
    }

    this.subscription.add(
      this.metadataService
        .getMetadata()
        .pipe(
          tap(({ columnGroups, shipmentColumns, containerColumns, orderColumns, orderExceptionColumns }) => {
            const columnsConfig: { [key: string]: MetadataColumn[] } = {
              Shipments: shipmentColumns,
              Containers: containerColumns,
              Orders: orderColumns,
              OrderExceptions: orderExceptionColumns
            };
            const columns = columnsConfig[this.tab!.type];
            this.treeNodes = this.treeBuilder.buildTree(columnGroups, columns);
          })
        )
        .subscribe()
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  configureTab() {
    this.subscription.add(
      this.matDialog
        .open(TabsConfiguratorComponent, {
          height: '90vh',
          maxWidth: '770px',
          width: '100%',
          disableClose: true,
          data: { tab: this.tab }
        })
        .afterClosed()
        .pipe(
          filter(response => response === 'Updated' || response === 'Deleted'),
          tap(response => this.tabsService.setTabsRefresh(response === 'Updated' ? this.tabIndex : 0))
        )
        .subscribe()
    );
  }

  onColumnSelect({ key }: ColumnEmitted) {
    this.selectedColumnKey = key;
  }

  search() {
    this.utilsTableService.updatePageConfig(this.tab!.id, {
      pageNumber: 1,
      search: {
        columnKey: this.selectedColumnKey,
        value: this.searchControl.value || ''
      }
    });
  }

  clearSearch() {
    this.searchControl.setValue('');
    this.search();
  }

  clearFilters() {
    this.selectedColumnKey = '';
    this.searchControl.setValue('');
    this.utilsTableService.resetPageConfig(this.tab!.id);
    this.cleared.emit();
  }

  editCustomFields() {
    this.subscription.add(
      this.metadataService
        .getMetadata()
        .pipe(
          switchMap(({ shipmentColumns, containerColumns }) => {
            const columns = this.tab?.type === 'Shipments' ? shipmentColumns : containerColumns;
            const columnKeySet = new Set(this.tab!.columns.map(({ columnKey }) => columnKey));
            const customColumns = columns.filter(col => col.custom && columnKeySet.has(col.key));
            return this.matDialog
              .open(CustomFieldsComponent, {
                width: '450px',
                disableClose: true,
                data: { selectedItems: this.selectedItems, columns: customColumns, type: this.tab!.type }
              })
              .afterClosed();
          })
        )
        .pipe()
        .subscribe()
    );
  }

  shareView() {
    this.matDialog.open(ShareViewComponent, {
      width: '420px',
      data: { tab: this.tab }
    });
  }
}
